import {
  Control,
  UseFormGetValues,
  UseFormWatch,
  useFieldArray,
} from 'react-hook-form';
import { maxBy, sumBy } from 'lodash';
import { revaluation as rules } from 'further-ui/validations';

import revaluationQueriesFactory from './revaluationQueriesFactory';
import { RevaluationPrefix } from 'further-types/lp/revaluation';
import { Revaluation } from '../../types';

const getCurrentUnitPrice = (
  revaluations: Array<Revaluation>,
  newRevaluation?: Partial<Revaluation>,
) => {
  if (newRevaluation) {
    return newRevaluation.unitPriceTcy;
  }

  if (revaluations.length > 0) {
    return maxBy(revaluations, (rev) => rev.ledgerDate)?.unitPriceTcy;
  }

  return 0;
};

const getCurrentHoldValue = (unitPriceTcy: number, numberOfUnits: number) => {
  return (unitPriceTcy ?? 0) * numberOfUnits;
};

type RevaluationHookProps = {
  watch: UseFormWatch<rules.RevaluationFields>;
  control: Control<rules.RevaluationFields>;
  getValues: UseFormGetValues<rules.RevaluationFields>;
};

const useRevaluations = ({
  watch,
  control,
  getValues,
}: RevaluationHookProps) => {
  const revaluationsWatch = watch('revaluations') ?? [];
  const newRevaluationsWatch = watch('newRevaluations') ?? [];
  const investmentHistory = watch('investmentHistory') ?? [];

  const revaluationFields = useFieldArray({
    control,
    name: 'revaluations',
  });
  const latestRevaluation: Revaluation | undefined =
    revaluationsWatch.length > 0
      ? maxBy(revaluationsWatch, (rev) => rev.ledgerDate)
      : undefined;

  const newRevaluations = useFieldArray({
    control,
    name: 'newRevaluations',
  });

  const assetCurrency = getValues('assetCurrency');

  const {
    getRevaluationsPerHoldingClass,
    getInvestmentsPerHoldingClass,
    getNewRevaluationPerHoldingClass,
    findRevaluationFieldIndex,
    getRevaluationsGroupedByHoldingClass,
    getAllRevaluations,
    getLatestRevaluationPerHoldingClass,
  } = revaluationQueriesFactory({
    revaluations: revaluationsWatch ?? [],
    investmentHistory,
    newRevaluations: newRevaluationsWatch,
    revaluationFormFields: revaluationFields.fields,
  });

  const calculateUnitPricePerHoldingClass = (holdingClassId: string) => {
    const newRevaluation = getNewRevaluationPerHoldingClass(holdingClassId);
    const revaluations = getRevaluationsPerHoldingClass(holdingClassId);
    return getCurrentUnitPrice(revaluations, newRevaluation);
  };

  const calculateHoldValuePerHoldingClass = (holdingClassId: string) => {
    const holdingClassInvestments = investmentHistory.filter(
      (investment) => investment?.holdingClassId === holdingClassId,
    );

    const numberOfUnits = sumBy(
      holdingClassInvestments,
      (investment) => investment.currentNumberOfUnits,
    );

    const currentUnitPrice = calculateUnitPricePerHoldingClass(holdingClassId);

    const currentHoldValue =
      numberOfUnits && currentUnitPrice
        ? numberOfUnits * currentUnitPrice
        : sumBy(
            holdingClassInvestments,
            (investment) => investment.currentUnrealisedValue,
          );

    return currentHoldValue;
  };

  const calculateTotalAssetHoldValue = () => {
    const groupedRevaluations = getRevaluationsGroupedByHoldingClass();
    return sumBy(Object.keys(groupedRevaluations), (groupId) =>
      calculateHoldValuePerHoldingClass(groupId),
    );
  };

  const calculateAssetInitialHoldValue = (
    numberOfUnits: number,
    index: number,
    prefix: RevaluationPrefix,
  ) => {
    return getCurrentHoldValue(
      getValues(`${prefix}.${index}.unitPriceTcy`) ?? 0,
      numberOfUnits,
    );
  };

  const shouldRenderNewRevaluation = (holdingClassId: string) => {
    const newRevaluations = getValues('newRevaluations') ?? [];
    const newRevaluation = newRevaluations.find(
      (rev) => rev.holdingClassId === holdingClassId,
    );

    return !!newRevaluation && !newRevaluation.isRemoved;
  };

  const deleteNewRevaluation = (holdingClassId: string) => {
    const newRevaluationIndex = newRevaluationsWatch.findIndex(
      (rev) => rev.holdingClassId === holdingClassId,
    );

    if (newRevaluationIndex !== -1) {
      newRevaluations.update(newRevaluationIndex, {
        ...newRevaluationsWatch[newRevaluationIndex],
        isRemoved: true,
      });
    }
  };

  const addNewRevaluation = (holdingClassId: string) => {
    const deletedRevaluationIndex = newRevaluationsWatch.findIndex(
      (rev) => rev.holdingClassId === holdingClassId && rev.isRemoved,
    );

    if (deletedRevaluationIndex !== -1) {
      newRevaluations.update(deletedRevaluationIndex, {
        ...newRevaluationsWatch[deletedRevaluationIndex],
        isRemoved: false,
      });
    }
  };

  return {
    latestRevaluation,
    getLatestRevaluationPerHoldingClass,
    getRevaluationsGroupedByHoldingClass,
    getInvestmentsPerHoldingClass,
    findRevaluationFieldIndex,
    calculateUnitPricePerHoldingClass,
    calculateAssetInitialHoldValue,
    calculateTotalAssetHoldValue,
    assetCurrency,
    getAllRevaluations,
    deleteNewRevaluation,
    addNewRevaluation,
    shouldRenderNewRevaluation,
  };
};

export default useRevaluations;
